import React from "react";
import PracticeMistakesDrill from "./PracticeMistakesDrill";
import { useLanguage } from "../i18n/LanguageContext";

const STATUS_STYLES = {
  ok:      { bg: "bg-forest-600/12",     text: "text-forest-700",      ring: "ring-forest-600/30",     label: "Correct" },
  partial: { bg: "bg-gold-500/22",       text: "text-gold-600",         ring: "ring-gold-500/40",       label: "Close · revise tajweed" },
  wrong:   { bg: "bg-terracotta-500/18", text: "text-terracotta-600",   ring: "ring-terracotta-500/40", label: "Mismatch" },
  missed:  { bg: "bg-sand-300/80",       text: "text-muted-foreground", ring: "ring-sand-300",          label: "Not recited" },
};

export default function MistakeHeatmap({ wordDiff, extras, surah, ayah }) {
  const { tx, t } = useLanguage();
  if (!wordDiff || wordDiff.length === 0) return null;

  const counts = wordDiff.reduce((acc, w) => {
    acc[w.status] = (acc[w.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="mistake-heatmap" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("mistakeHeatmap.mistakeHeatmap")}</div>
          <div className="text-sm text-foreground/80 mt-1">
            {t("mistakeHeatmap.eachWordColorCodedBy")}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">{t("mistakeHeatmap.words", { wordDiffCount: wordDiff.length })}</div>
      </div>

      {/* Heatmap text */}
      <div
        dir="rtl"
        lang="ar"
        className="font-arabic text-3xl md:text-4xl leading-[2.4] rounded-2xl bg-white border border-sand-300 p-6 text-right"
      >
        {wordDiff.map((w, i) => {
          const s = STATUS_STYLES[w.status] || STATUS_STYLES.missed;
          return (
            <span
              key={i}
              data-testid={`hm-word-${i}-${w.status}`}
              className={`inline-block mx-1 my-1 px-2 py-0.5 rounded-md ${s.bg} ${s.text} ring-1 ${s.ring} transition`}
              title={tx(s.label)}
            >
              {w.word}
            </span>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(STATUS_STYLES).map(([k, s]) => (
          <div key={k} className="inline-flex items-center gap-2" data-testid={`legend-${k}`}>
            <span className={`h-3 w-3 rounded-sm ${s.bg} ring-1 ${s.ring}`} />
            <span className="text-foreground/80">{tx(s.label)}</span>
            <span className="text-muted-foreground">· {counts[k] || 0}</span>
          </div>
        ))}
      </div>

      {/* Practice mistakes drill */}
      <div className="pt-1">
        <PracticeMistakesDrill surah={surah} ayah={ayah} wordDiff={wordDiff} />
      </div>

      {/* Extras */}
      {extras && extras.length > 0 && (
        <div className="rounded-xl border border-sand-300 bg-sand-200/50 p-4" data-testid="hm-extras">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
            {t("mistakeHeatmap.extraWordsWeHeard", { extrasCount: extras.length })}
          </div>
          <div dir="rtl" className="font-arabic text-xl text-foreground/80 leading-relaxed">
            {extras.map((w, i) => (
              <span key={i} className="inline-block mx-1 px-2 rounded bg-white border border-sand-300">
                {w}
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("mistakeHeatmap.theseWereTranscribedButDon")}
          </p>
        </div>
      )}
    </div>
  );
}
